import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { useLessonPrivileges } from './lesson-privileges.hook'

interface LessonsAddMenuReturn {
  refIsOpen: Ref<boolean>
  refContainer: Ref<HTMLElement | null>
  refFloating: Ref<HTMLElement | null>
  refFloatingStyles: Ref<CSSProperties>
  refCanCreateRecurring: ComputedRef<boolean>
  singleUrl: string
  recurringUrl: string
  toggle: () => void
  close: () => void
}

/** "Add lesson" on the Lessons tab: a menu leading to the single or the recurring create page. Recurring is offered
 *  only to those who may create recurring events, as on the create page's own tabs. */
export const useLessonsAddMenu = (): LessonsAddMenuReturn => {
  const route = useRoute()
  const { canCreateRecurring } = useLessonPrivileges()

  const disciplineId = route.params.id as string

  const singleUrl = resolveUrl(AppRoute.LessonsCreate, { id: disciplineId }) ?? ''
  const recurringUrl = resolveUrl(AppRoute.LessonsCreateRecurring, { id: disciplineId }) ?? ''

  const refIsOpen = ref<boolean>(false)
  const refContainer = ref<HTMLElement | null>(null)
  const refFloating = ref<HTMLElement | null>(null)

  const { floatingStyles: refFloatingStyles } = useFloating(refContainer, refFloating, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const toggle = (): void => {
    refIsOpen.value = !refIsOpen.value
  }

  const close = (): void => {
    refIsOpen.value = false
  }

  onClickOutside(refContainer, close)

  return {
    refIsOpen,
    refContainer,
    refFloating,
    refFloatingStyles,
    refCanCreateRecurring: canCreateRecurring,
    singleUrl,
    recurringUrl,
    toggle,
    close,
  }
}
